import React, { FC, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import dompurify from 'dompurify';

import defaultHeroImage from '../../../assets/Itv/girl.png';
import itvGreen from '../../../assets/Itv/itv-green.svg';
import footerLogo from '../../../assets/Itv/Win_White 1.svg';
import itvBlue from '../../../assets/Itv/itv-blue.svg';

import { Footer } from '../../../components/Footer';
import { useGetLandingBySlugQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-landing-by-slug.query.generated';
import { APP_URLS, getPrizesUrl, locale } from '../../../consts';
import { ReactComponent as PrizesLogo } from '../../../assets/Itv/Prizes-White.svg';
import { filterSpecialHTMLCharacters } from '../../../utils/string';
import { Endpoint } from '../../../models/api.model';
import { getImageUrl } from '../../../utils/navigation';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { useSignIn } from '../../../hooks/auth';
import { BRANDS } from '../../../components/Itv/types';
import { useUserContext } from '../../../providers/UserProvider';

import { ThemeVariant, comboHeroCtas, STEP_OR_SEPARATOR } from './constants';
import { getUtmParamsForBrand } from './utils/utm';
import { StepDescription } from './components/StepDescription';
import './ItvStaticLanding.scss';
import { getRedirectUri } from '../../../components/Authentification/helper';
import { SEO } from '../../../components/seo/SEO';

export const ItvStaticLanding: FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const isMobileDevice = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);
  const signIn = useSignIn();
  const { setTheme } = useUserContext();
  const { data, loading } = useGetLandingBySlugQuery({
    variables: {
      language: locale,
      slug: slug ?? '',
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
    skip: !slug,
  });

  const [landingPageData] = data?.Landing ?? [];
  const themeVariant = landingPageData?.theme as ThemeVariant | undefined;
  const translations = landingPageData?.translations?.length ? landingPageData.translations[0] : null;
  const [brand] = themeVariant?.split('-') ?? [''];
  const isComboVariant = Boolean(themeVariant?.includes('combo'));

  useEffect(() => {
    if (!themeVariant) return;

    const nextTheme = isComboVariant ? BRANDS.BINGO : (brand as BRANDS);

    setTheme(nextTheme);
  }, [brand, isComboVariant, setTheme, themeVariant]);

  if (!slug) return <span>404</span>;

  if (loading && !data) return <span>Loading</span>;

  if (!landingPageData) return <span>404</span>;

  if (!translations || !themeVariant) return <span>404</span>;
  const rootClassName = ['itv-static-landing', `itv-static-landing--${themeVariant} ${brand}`].join(' ');
  const logo = themeVariant.includes('bingo') ? itvGreen : themeVariant.includes('spins') ? itvBlue : footerLogo;
  const steps = [
    { number: '1', description: translations.hero_first_step },
    { number: '2', description: translations.hero_second_step },
    { number: '3', description: translations.hero_third_step },
  ];
  const sanitizer = dompurify.sanitize;
  const heroImage = landingPageData.hero_image
    ? getImageUrl(landingPageData.hero_image?.filename_disk)
    : defaultHeroImage;
  const heroImageMobile = landingPageData.hero_image_mobile
    ? getImageUrl(landingPageData.hero_image_mobile?.filename_disk)
    : heroImage;
  const heroImg = isMobileDevice ? heroImageMobile : heroImage;

  const handleLogin = (): void => {
    const theme = isComboVariant ? BRANDS.BINGO : (brand as BRANDS);

    signIn({
      redirectUri: getRedirectUri(`/${theme}`),
    });
  };

  const backToPrizesUrl = getPrizesUrl();

  return (
    <>
      <SEO description={landingPageData.SEO_description} title={landingPageData.SEO_title} />

      <div className={rootClassName}>
        <header className="itv-static-landing__header">
          <div className="itv-static-landing__header-primary">
            <img
              src={logo}
              alt="ITV Win Bingo"
              width={59}
              height={54}
              className={!isComboVariant ? 'header-logo-large' : ''}
            />
            <div className="itv-static-landing__auth-actions" aria-label="Authentication actions">
              <button
                type="button"
                onClick={handleLogin}
                className={`itv-static-landing__button itv-static-landing__button--transparent-${isComboVariant ? 'white' : 'accent'}`}
              >
                Login
              </button>
              {landingPageData.show_go_to_prize_btn && (
                <Link to={backToPrizesUrl}>
                  <button
                    type="button"
                    className={`itv-static-landing__button itv-static-landing__button--brand-link itv-static-landing__button--transparent-${isComboVariant ? 'accent' : 'white'}`}
                  >
                    <PrizesLogo className="itv-static-landing__prizes-logo" />
                  </button>
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="itv-static-landing__main">
          <section className="itv-static-landing__hero" style={{ backgroundImage: `url(${heroImg}` }}>
            <div className="itv-static-landing__container itv-static-landing__container--hero itv-static-landing__hero-grid">
              <div className="itv-static-landing__hero-copy">
                {isComboVariant ? (
                  <div className="itv-static-landing__combo-variant">
                    <div
                      className="itv-static-landing__combo-banner itv-static-landing__combo-banner"
                      role="presentation"
                    >
                      <span>
                        Choose your <br />
                        welcome reward
                      </span>
                    </div>
                    <div className="itv-static-landing__cta-groups">
                      <div className="itv-static-landing__cta-group" role="group" aria-label="Sign up options">
                        <h1
                          dangerouslySetInnerHTML={{
                            __html: filterSpecialHTMLCharacters(sanitizer(translations.hero_heading || '')),
                          }}
                        ></h1>
                        <Link
                          key={comboHeroCtas[0].id}
                          to={`/${comboHeroCtas[0].id}${APP_URLS.registration}${getUtmParamsForBrand(searchParams, isComboVariant, landingPageData, comboHeroCtas[0].id as 'bingo' | 'spins')}`}
                        >
                          <button
                            type="button"
                            className={[
                              'itv-static-landing__cta',
                              `itv-static-landing__cta--${comboHeroCtas[0].id}`,
                            ].join(' ')}
                          >
                            {comboHeroCtas[0].label}
                          </button>
                        </Link>
                      </div>
                      <div className="itv-static-landing__cta-group" role="group" aria-label="Sign up options">
                        <h1
                          dangerouslySetInnerHTML={{
                            __html: filterSpecialHTMLCharacters(sanitizer(translations.hero_heading_2_combo || '')),
                          }}
                        ></h1>
                        <Link
                          key={comboHeroCtas[1].id}
                          to={`/${comboHeroCtas[1].id}${APP_URLS.registration}${getUtmParamsForBrand(searchParams, isComboVariant, landingPageData, comboHeroCtas[1].id as 'bingo' | 'spins')}`}
                        >
                          <button
                            type="button"
                            className={[
                              'itv-static-landing__cta',
                              `itv-static-landing__cta--${comboHeroCtas[1].id}`,
                            ].join(' ')}
                          >
                            {comboHeroCtas[1].label}
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <h1
                      dangerouslySetInnerHTML={{
                        __html: filterSpecialHTMLCharacters(sanitizer(translations.hero_heading || '')),
                      }}
                    ></h1>
                    <Link
                      to={`/${brand}${APP_URLS.registration}${getUtmParamsForBrand(
                        searchParams,
                        isComboVariant,
                        landingPageData,
                        brand as 'bingo' | 'spins'
                      )}`}
                    >
                      <button type="button" className="itv-static-landing__cta">
                        Sign Up
                      </button>
                    </Link>
                    <p
                      className="itv-static-landing__disclaimer"
                      dangerouslySetInnerHTML={{
                        __html: filterSpecialHTMLCharacters(sanitizer(translations.hero_terms || '')),
                      }}
                    ></p>
                    <p className="itv-static-landing__hero-body">{translations.hero_description}</p>
                  </>
                )}
              </div>
            </div>
            {isComboVariant ? (
              <p
                className="itv-static-landing__disclaimer itv-static-landing__disclaimer--hero-bottom"
                dangerouslySetInnerHTML={{
                  __html: filterSpecialHTMLCharacters(sanitizer(translations.hero_terms || '')),
                }}
              ></p>
            ) : null}
          </section>

          {isComboVariant ? null : (
            <section className="itv-static-landing__steps">
              <div className="itv-static-landing__container itv-static-landing__container--hero itv-static-landing__steps-inner">
                <div className="itv-static-landing__steps-mobile">
                  {steps.map((step) => {
                    const stepText = step.description ?? '';

                    return (
                      <div key={step.number} className="itv-static-landing__steps-mobile-row">
                        <div className="itv-static-landing__step-icon">
                          <span>{step.number}</span>
                        </div>
                        <div className="itv-static-landing__step-body">
                          <StepDescription text={stepText} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="itv-static-landing__steps-desktop">
                  {steps.map((step) => {
                    const stepText = step.description ?? '';
                    const hasOr = STEP_OR_SEPARATOR.test(stepText);
                    const desktopCardClassName = [
                      'itv-static-landing__steps-desktop-card',
                      hasOr ? 'itv-static-landing__steps-desktop-card--with-or' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <div key={step.number} className={desktopCardClassName}>
                        <div className="itv-static-landing__step-icon">
                          <span>{step.number}</span>
                        </div>
                        <div className="itv-static-landing__step-body">
                          <StepDescription text={stepText} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          <section className="itv-static-landing__terms">
            <div className="itv-static-landing__container itv-static-landing__container--hero">
              <div className="itv-static-landing__terms-content">
                <div
                  className={`itv-static-landing__terms-toggle${' itv-static-landing__terms-toggle--open'}`}
                  aria-expanded
                >
                  <span aria-hidden="true">{'+'}</span>
                  <span>Full Terms &amp; Conditions</span>
                </div>
                <div className={`itv-static-landing__terms-panel${' itv-static-landing__terms-panel--open'}`}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: filterSpecialHTMLCharacters(sanitizer(translations.full_terms)),
                    }}
                  ></p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer isLangSwitcherDisplayed={false} isTimeDisplayed={false} />
      </div>
    </>
  );
};
